<template>
  <section
    class="company-card text-gray-900 flex flex-col bg-white border-t border-b border-gray-200 shadow-sm p-2 overflow-auto z-10 lg_border lg_border-gray-400 lg_rounded-md lg_shadow-normal lg_p-6"
    data-intercom-target="company-card"
    :style="{ '--height-clamp-mob-only': `calc(100vh - ${headerHeight}px)` }"
  >
    <img
      v-if="company.logo"
      class="company-logo hidden h-12 mb-3 self-start border border-gray-100 rounded-md object-contain xl1_block"
      :src="company.logo"
      :alt="company.name"
      data-testid="company-card-logo"
    />
    <div class="flex items-center xl1_justify-between xl1_flex-wrap">
      <button
        v-if="company.description || company.website"
        class="appearance-none p-0 bg-transparent mr-4 xl1_transform xl1_rotate-180 xl1_order-2 xl1_mr-0 xl1_absolute xl1_top-6 xl1_right-6"
        data-testid="expand-company-info-btn"
        type="button"
        @click="isOpen = !isOpen"
      >
        <ChevronDownIcon v-if="isOpen" class="w-6" />
        <ChevronUpIcon v-else class="w-6" />
      </button>
      <div class="font-medium">
        <p class="font-normal mb-0 w-full text-lg">{{ company.name }}</p>
        <div class="flex items-center text-xs">
          <p class="text-gray-700 mb-0 mr-2">{{ company.stock_symbol }}</p>
          <div v-if="company.last_quote" class="flex items-center" data-testid="company-card-stock-info">
            <p class="font-bold mb-0 mr-2">${{ formatTradeValue(company.last_quote.last) }}</p>
            <CompaniesStockChange class="py-1 px-1.5" :stock="company.last_quote" />
          </div>
        </div>
      </div>
    </div>
    <div
      class="additional-company-info"
      :class="{ open: isOpen }"
      :style="{ maxHeight: `${boxHeight}px` }"
      data-testid="additional-company-info"
    >
      <div ref="additionalInfoBox" class="pt-6">
        <p class="whitespace-pre-line">{{ company.description }}</p>
        <a v-if="company.website" class="text-sm mt-4 mb-0 text-gray-900 flex" :href="company.website" target="_blank">
          <LinkIcon class="w-4 mr-2" />{{ company.website }}
        </a>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue'
import { LinkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/outline'
import type { Company, IncludeLastQuote } from '@/entities/company'
import { formatTradeValue } from '@/lib/utils'

defineProps({
  company: {
    type: Object as PropType<Company & IncludeLastQuote>,
    required: true,
  },
})

const isOpen: Ref<boolean> = ref(false)
const headerHeight = computed(() => document.querySelector('#navbar')?.getBoundingClientRect()?.bottom ?? 0)
const { element: additionalInfoBox, contentHeight } = useObserveHeight()
const boxHeight = computed(() => (isOpen.value ? contentHeight.value : 0))
</script>

<style lang="stylus" scoped>
.company-card
  max-height var(--height-clamp-mob-only)

@screen lg
  .company-card
    max-height unset

.company-logo
  aspect-ratio 16 / 9
  border-style solid

.additional-company-info
  overflow-y auto
  transition max-height 200ms

@screen lg
  .additional-company-info
    overflow-y hidden

.collapsed-buttons
  transition all 0.2s

// TODO: Fixme in VRFY-5972
.px-override
  padding-left 0.5rem !important
  padding-right 0.5rem !important
</style>
